/**
 * Plot Matrix (JSON) dev-tool — "Plot View" column grouping.
 *
 * Mirrors `PlotMatrix.tsx`'s own `generateTableColumns` grouping rule exactly
 * (group by the text before the first " - " in `categoryName`; a group with
 * exactly one column whose `categoryName` had no " - " at all renders flat,
 * titled by the main header alone — everything else nests under a shared
 * main-header column group) so `PlotMatrixJsonPlotTable.tsx` produces the
 * same column layout as the live embedded Plot Matrix's default view.
 */
import type { PlotMatrixCategoryColumn } from './plotMatrixCategoryGenerator';

export interface PlotMatrixPlotViewColumn {
  column: PlotMatrixCategoryColumn;
  subHeader: string;
}

export interface PlotMatrixPlotViewGroup {
  mainHeader: string;
  /** True when this group is a single column with no "Main - Sub" split —
   *  render it as one flat leaf column titled `mainHeader`, not nested. */
  flat: boolean;
  columns: PlotMatrixPlotViewColumn[];
}

export function groupPlotMatrixColumnsForPlotView(
  columns: PlotMatrixCategoryColumn[],
): PlotMatrixPlotViewGroup[] {
  const order: string[] = [];
  const groups = new Map<string, PlotMatrixPlotViewGroup>();

  columns.forEach((column) => {
    const parts = column.categoryName.split(' - ');
    const hasSeparator = parts.length > 1;
    const mainHeader = parts[0] || column.categoryName;
    const subHeader = parts[1] || column.categoryName;

    if (!groups.has(mainHeader)) {
      groups.set(mainHeader, { mainHeader, flat: true, columns: [] });
      order.push(mainHeader);
    }
    const group = groups.get(mainHeader)!;
    group.columns.push({ column, subHeader });
    if (hasSeparator) group.flat = false;
  });

  order.forEach((key) => {
    const group = groups.get(key)!;
    if (group.columns.length !== 1) group.flat = false;
  });

  return order.map((key) => groups.get(key)!);
}
